<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">New Visit</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a @click="goTo('queue-management')">Reception</a></li>
                      <li class="breadcrumb-item active">New Visit</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Left: Patient Search & Selection -->
              <div class="col-lg-5">
                <div class="card">
                  <div class="card-body p-4">
                    <!-- Header -->
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-account-search me-2 text-primary"></i>Select Patient</h5>
                        <small class="text-muted">Search and select a patient for the visit</small>
                      </div>
                    </div>

                    <!-- Search Input -->
                    <div class="search-input-wrapper mb-3">
                      <div class="input-group search-box">
                          <span class="input-group-text">
                            <i v-if="searching" class="mdi mdi-loading mdi-spin text-primary"></i>
                            <i v-else class="mdi mdi-magnify text-muted"></i>
                          </span>
                        <input
                            type="text"
                            class="form-control"
                            v-model="searchTerm"
                            placeholder="Type patient name, ID, phone..."
                            ref="searchInput"
                            @keydown.esc="clearSearch"
                        >
                        <button v-if="searchTerm" class="btn btn-light border-start-0" @click="clearSearch" type="button">
                          <i class="mdi mdi-close"></i>
                        </button>
                      </div>
                      <small v-if="searchTerm && searchTerm.length < 2" class="text-warning mt-1 d-block">
                        <i class="mdi mdi-information me-1"></i>Type at least 2 characters to search
                      </small>
                      <small v-else-if="!searchTerm" class="text-muted mt-1 d-block">
                        <i class="mdi mdi-lightbulb-outline me-1"></i>
                        Try: <span class="search-example">John Doe</span>, <span class="search-example">12345678</span>, <span class="search-example">0712...</span>
                      </small>
                    </div>

                    <!-- Search Results -->
                    <div v-if="searchResults.length > 0 && !selectedPatient" class="search-results mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <small class="text-muted">Search Results ({{ searchResults.length }})</small>
                      </div>
                      <div class="list-group list-group-flush">
                        <a
                            v-for="patient in searchResults"
                            :key="patient.patientID"
                            href="javascript:void(0);"
                            class="list-group-item list-group-item-action py-3"
                            @click="selectPatient(patient)"
                        >
                          <div class="d-flex align-items-center">
                            <div class="avatar-sm me-3">
                                <span class="avatar-title rounded-circle" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'">
                                  {{ getInitials(patient.fullNames) }}
                                </span>
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0">{{ patient.fullNames }}</h6>
                              <small class="text-muted">
                                {{ patient.patientID }} &bull; {{ patient.gender }} &bull; {{ calculateAge(patient.dateOfBirth) }} yrs
                              </small>
                            </div>
                            <i class="mdi mdi-chevron-right text-muted"></i>
                          </div>
                        </a>
                      </div>
                    </div>

                    <!-- No Results -->
                    <div v-else-if="hasSearched && searchResults.length === 0 && !selectedPatient" class="text-center py-4">
                      <div class="empty-state-icon mb-3">
                        <i class="mdi mdi-account-search-outline"></i>
                      </div>
                      <p class="text-muted mb-2">No patients found</p>
                      <button class="btn btn-outline-primary btn-sm" @click="goTo('patient-register')">
                        <i class="mdi mdi-account-plus me-1"></i> Register New Patient
                      </button>
                    </div>

                    <!-- Selected Patient Card -->
                    <div v-if="selectedPatient" class="selected-patient-card">
                      <div class="card border mb-0">
                        <div class="card-body">
                          <div class="d-flex align-items-start">
                            <div class="avatar-md me-3">
                                <span class="avatar-title rounded-circle" :class="selectedPatient.gender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.2rem;">
                                  {{ getInitials(selectedPatient.fullNames) }}
                                </span>
                            </div>
                            <div class="flex-grow-1">
                              <div class="d-flex justify-content-between align-items-start">
                                <div>
                                  <h5 class="mb-1">{{ selectedPatient.fullNames }}</h5>
                                  <div class="d-flex flex-wrap gap-1 mb-2">
                                    <span class="badge bg-light text-dark">{{ selectedPatient.patientID }}</span>
                                    <span class="badge" :class="selectedPatient.gender === 'Male' ? 'bg-primary-subtle text-primary' : 'bg-pink-subtle text-pink'">{{ selectedPatient.gender }}</span>
                                    <span class="badge bg-light text-dark">{{ calculateAge(selectedPatient.dateOfBirth) }} years</span>
                                  </div>
                                  <p class="mb-0 text-muted small">
                                    <i class="mdi mdi-phone me-1"></i>{{ selectedPatient.phoneNumber || 'N/A' }}
                                  </p>
                                </div>
                                <button class="btn btn-sm btn-outline-secondary" @click="clearPatient" title="Change Patient">
                                  <i class="mdi mdi-close"></i>
                                </button>
                              </div>

                              <!-- Medical Alerts -->
                              <div v-if="selectedPatient.allergies || selectedPatient.chronicConditions" class="mt-3 pt-3 border-top">
                                <div v-if="selectedPatient.allergies" class="alert alert-danger py-2 mb-2">
                                  <small><i class="mdi mdi-alert-circle me-1"></i><strong>Allergies:</strong> {{ selectedPatient.allergies }}</small>
                                </div>
                                <div v-if="selectedPatient.chronicConditions" class="alert alert-warning py-2 mb-0">
                                  <small><i class="mdi mdi-heart-pulse me-1"></i><strong>Chronic:</strong> {{ selectedPatient.chronicConditions }}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Quick Actions when no patient selected -->
                    <div v-if="!selectedPatient && !hasSearched" class="mt-4">
                      <small class="text-muted d-block mb-2">Quick Actions</small>
                      <div class="d-grid gap-2">
                        <button class="btn btn-outline-primary" @click="goTo('patient-register')">
                          <i class="mdi mdi-account-plus me-1"></i> Register New Patient
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Recent Visits for Selected Patient -->
                <div class="card" v-if="selectedPatient && recentVisits.length > 0">
                  <div class="card-header bg-white py-3">
                    <h6 class="mb-0"><i class="mdi mdi-history me-2 text-primary"></i>Recent Visits</h6>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive">
                      <table class="table table-sm table-hover mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="visit in recentVisits.slice(0, 5)" :key="visit.visitID">
                          <td class="small">{{ formatDate(visit.dateCreated) }}</td>
                          <td><span class="badge bg-light text-dark">{{ visit.visitType }}</span></td>
                          <td>
                                <span class="badge" :class="getStatusBadge(visit.visitStatus)">
                                  {{ formatStatus(visit.visitStatus) }}
                                </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Visit Details Form -->
              <div class="col-lg-7">
                <div class="card">
                  <div class="card-body p-4">
                    <!-- Header -->
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-clipboard-plus me-2 text-primary"></i>Visit Details</h5>
                        <small class="text-muted">Enter visit information</small>
                      </div>
                    </div>

                    <form @submit.prevent="createVisit">
                      <div class="row">
                        <!-- Visit Type -->
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Visit Type <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="visit.visitType" required>
                            <option value="">Select Visit Type</option>
                            <option value="OPD">OPD - Outpatient</option>
                            <option value="EMERGENCY">Emergency</option>
                            <option value="REFERRAL">Referral</option>
                            <option value="FOLLOW_UP">Follow Up</option>
                            <option value="MCH">MCH - Maternal Child Health</option>
                            <option value="DENTAL">Dental</option>
                            <option value="SPECIALIST">Specialist Clinic</option>
                          </select>
                        </div>

                        <!-- Visit Category -->
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Category</label>
                          <select class="form-select" v-model="visit.visitCategory">
                            <option value="GENERAL">General</option>
                            <option value="PEDIATRIC">Pediatric</option>
                            <option value="ANTENATAL">Antenatal</option>
                            <option value="POSTNATAL">Postnatal</option>
                            <option value="SURGICAL">Surgical</option>
                            <option value="MEDICAL">Medical</option>
                          </select>
                        </div>

                        <!-- Department -->
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Department</label>
                          <select class="form-select" v-model="visit.departmentID">
                            <option value="">Select Department</option>
                            <option v-for="dept in departments" :key="dept.departmentID" :value="dept.departmentID">
                              {{ dept.departmentName }}
                            </option>
                          </select>
                        </div>

                        <!-- Payment Mode -->
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Payment Mode <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="visit.paymentMode" required>
                            <option value="">Select Payment Mode</option>
                            <option value="CASH">Cash</option>
                            <option value="INSURANCE">Insurance</option>
                            <option value="NHIF">NHIF</option>
                            <option value="CORPORATE">Corporate</option>
                            <option value="WAIVER">Waiver</option>
                          </select>
                        </div>

                        <!-- Insurance Selection (if applicable) -->
                        <div class="col-12 mb-3" v-if="visit.paymentMode === 'INSURANCE' || visit.paymentMode === 'NHIF'">
                          <label class="form-label">Select Insurance</label>
                          <div v-if="patientInsurances.length > 0">
                            <div class="row g-2">
                              <div class="col-md-6" v-for="ins in patientInsurances" :key="ins.insuranceID">
                                <div
                                    class="insurance-option p-3 border rounded"
                                    :class="{ 'border-primary selected': visit.insuranceID === ins.insuranceID }"
                                    @click="visit.insuranceID = ins.insuranceID"
                                >
                                  <div class="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h6 class="mb-1">{{ ins.insuranceProvider }}</h6>
                                      <small class="text-muted d-block">{{ ins.memberNumber }}</small>
                                      <div class="mt-2">
                                          <span class="badge" :class="ins.verificationStatus === 'Verified' ? 'bg-success' : 'bg-warning'">
                                            {{ ins.verificationStatus }}
                                          </span>
                                      </div>
                                    </div>
                                    <div class="form-check">
                                      <input
                                          class="form-check-input"
                                          type="radio"
                                          :value="ins.insuranceID"
                                          v-model="visit.insuranceID"
                                      >
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else class="alert alert-warning mb-0">
                            <i class="mdi mdi-alert me-2"></i>
                            No insurance records found.
                            <a href="javascript:void(0);" @click="goToPatientInsurance" class="alert-link">Add Insurance</a>
                          </div>
                        </div>

                        <!-- Referral Number (if referral) -->
                        <div class="col-md-6 mb-3" v-if="visit.visitType === 'REFERRAL'">
                          <label class="form-label">Referral Number</label>
                          <input type="text" class="form-control" v-model="visit.referralNo" placeholder="Enter referral number">
                        </div>

                        <!-- Chief Complaint -->
                        <div class="col-12 mb-3">
                          <label class="form-label">Chief Complaint <span class="text-danger">*</span></label>
                          <textarea
                              class="form-control"
                              rows="3"
                              v-model="visit.chiefComplaint"
                              placeholder="Brief description of patient's main complaint..."
                              required
                          ></textarea>
                        </div>
                      </div>

                      <!-- Consultation Fee Section -->
                      <div class="border rounded p-3 mb-3 bg-light">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h6 class="mb-0"><i class="mdi mdi-receipt me-2 text-success"></i>Consultation Fee</h6>
                          <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox" id="billConsultation" v-model="billConsultation">
                            <label class="form-check-label small" for="billConsultation">Bill consultation</label>
                          </div>
                        </div>

                        <div v-if="billConsultation">
                          <div class="row g-2">
                            <!-- Income Type Selection -->
                            <div class="col-md-4">
                              <label class="form-label small">Income Type</label>
                              <select class="form-select form-select-sm" v-model="selectedIncomeTypeId" @change="onIncomeTypeChange">
                                <option value="">-- Select Type --</option>
                                <option v-for="type in incomeTypes" :key="type.incomeTypeId" :value="type.incomeTypeId">
                                  {{ type.incomeTypeDescription }}
                                </option>
                              </select>
                            </div>

                            <!-- Fee Selection -->
                            <div class="col-md-5">
                              <label class="form-label small">Select Fee</label>
                              <select class="form-select form-select-sm" v-model="selectedConsultationFee" @change="onConsultationFeeChange" :disabled="!selectedIncomeTypeId || loadingFees">
                                <option value="">-- Select Fee --</option>
                                <option v-for="fee in consultationFees" :key="fee.feeId" :value="JSON.stringify(fee)">
                                  {{ fee.feeDescription }} - KES {{ formatCurrency(fee.unitFeeAmount) }}
                                </option>
                              </select>
                              <small v-if="loadingFees" class="text-muted">
                                <i class="mdi mdi-loading mdi-spin me-1"></i>Loading fees...
                              </small>
                              <small v-else-if="selectedIncomeTypeId && consultationFees.length === 0" class="text-warning">
                                <i class="mdi mdi-alert me-1"></i>No fees found for this type
                              </small>
                            </div>

                            <!-- Amount -->
                            <div class="col-md-3">
                              <label class="form-label small">Amount</label>
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">KES</span>
                                <input type="text" class="form-control bg-white fw-bold text-success"
                                       :value="formatCurrency(consultationAmount)" readonly>
                              </div>
                            </div>
                          </div>

                          <!-- Fee Info Alert -->
                          <div v-if="selectedFeeData" class="alert alert-info py-2 mt-2 mb-0 small">
                            <i class="mdi mdi-information me-1"></i>
                            Invoice will be generated for <strong>{{ selectedFeeData.feeDescription }}</strong>.
                            Patient can pay at cashier anytime.
                          </div>
                        </div>

                        <div v-else class="text-muted small">
                          <i class="mdi mdi-information-outline me-1"></i>
                          Toggle on to bill consultation fee with this visit.
                        </div>
                      </div>

                      <!-- Action Buttons -->
                      <div class="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                        <button type="button" class="btn btn-light" @click="resetForm">
                          <i class="mdi mdi-refresh me-1"></i> Reset
                        </button>
                        <button
                            type="submit"
                            class="btn btn-primary"
                            :disabled="!canSubmit || submitting"
                        >
                            <span v-if="submitting">
                              <i class="mdi mdi-loading mdi-spin me-1"></i> Creating...
                            </span>
                          <span v-else>
                              <i class="mdi mdi-check me-1"></i> Create Visit
                              <span v-if="billConsultation && consultationAmount > 0" class="ms-1">
                                (+ KES {{ formatCurrency(consultationAmount) }})
                              </span>
                            </span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Success Modal -->
    <div class="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center py-5">
            <div class="success-checkmark mb-4">
              <i class="mdi mdi-check-circle text-success" style="font-size: 80px;"></i>
            </div>
            <h4 class="mb-2">Visit Created Successfully!</h4>
            <p class="text-muted mb-1">Patient: <strong>{{ selectedPatient?.fullNames }}</strong></p>
            <p class="text-muted mb-3">Visit No: <strong>{{ createdVisit?.visitNo }}</strong></p>

            <div class="queue-number-box mb-3">
              <span class="text-muted d-block mb-1">Queue Number</span>
              <div class="queue-number">{{ createdVisit?.queueNo }}</div>
            </div>

            <!-- Bill Info (if consultation was billed) -->
            <div v-if="generatedBillNo" class="mb-4">
              <div class="alert alert-success py-2 mb-2">
                <i class="mdi mdi-receipt me-1"></i>
                Invoice Generated: <strong>{{ generatedBillNo }}</strong>
              </div>
              <h5 class="text-success mb-0">KES {{ formatCurrency(consultationAmount) }}</h5>
              <small class="text-muted">Consultation Fee - Pending Payment</small>
            </div>

            <div class="d-flex justify-content-center gap-2 flex-wrap">
              <button class="btn btn-outline-secondary" @click="printTicket">
                <i class="mdi mdi-printer me-1"></i> Print Ticket
              </button>
              <button v-if="generatedBillNo" class="btn btn-outline-success" @click="printBill">
                <i class="mdi mdi-receipt me-1"></i> Print Invoice
              </button>
              <button class="btn btn-outline-primary" @click="newVisit">
                <i class="mdi mdi-plus me-1"></i> New Visit
              </button>
              <button class="btn btn-primary" @click="goToQueue">
                <i class="mdi mdi-format-list-bulleted me-1"></i> View Queue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital, biller } from "@/api";

export default {
  name: "NewVisit",
  components: { NavigationBar, ToastNotification },
  props: {
    patientID: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // Search
      searchTerm: '',
      searchResults: [],
      searching: false,
      hasSearched: false,
      searchTimeout: null,

      // Selected Patient
      selectedPatient: null,
      patientInsurances: [],
      recentVisits: [],

      // Visit Form
      visit: {
        visitType: '',
        visitCategory: 'GENERAL',
        departmentID: '',
        paymentMode: '',
        insuranceID: '',
        chiefComplaint: '',
        referralNo: ''
      },

      // Dropdowns
      departments: [],

      // Consultation Fee Billing
      billConsultation: true,
      loadingFees: false,
      incomeTypes: [],
      selectedIncomeTypeId: '',
      consultationFees: [],
      selectedConsultationFee: '',
      selectedFeeData: null,
      consultationAmount: 0,
      generatedBillNo: '',

      // State
      submitting: false,
      createdVisit: null,

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      zone: ''
    };
  },
  computed: {
    canSubmit() {
      return this.selectedPatient &&
          this.visit.visitType &&
          this.visit.paymentMode &&
          this.visit.chiefComplaint;
    }
  },
  watch: {
    searchTerm(val) {
      // Clear previous timeout
      if (this.searchTimeout) clearTimeout(this.searchTimeout);

      // Reset if empty or too short
      if (!val || val.trim().length < 2) {
        this.searchResults = [];
        this.hasSearched = false;
        return;
      }

      // Debounce search - wait 300ms after user stops typing
      this.searchTimeout = setTimeout(() => {
        this.searchPatients();
      }, 300);
    }
  },
  beforeUnmount() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    calculateAge(dob) {
      if (!dob) return '-';
      const today = new Date();
      const birth = new Date(dob);
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatCurrency(amount) {
      return 'KES ' + Number(amount || 0).toLocaleString();
    },

    getStatusBadge(status) {
      const badges = {
        'WAITING_TRIAGE': 'bg-warning text-dark',
        'IN_TRIAGE': 'bg-info',
        'WAITING_CONSULTATION': 'bg-primary',
        'IN_CONSULTATION': 'bg-info',
        'WAITING_LAB': 'bg-secondary',
        'WAITING_PHARMACY': 'bg-secondary',
        'COMPLETED': 'bg-success',
        'CANCELLED': 'bg-danger'
      };
      return badges[status] || 'bg-secondary';
    },

    formatStatus(status) {
      if (!status) return '-';
      return status.replace(/_/g, ' ').replace(/WAITING/g, 'Wait').replace(/IN/g, 'In');
    },

    clearSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTerm = '';
      this.searchResults = [];
      this.hasSearched = false;
      this.$refs.searchInput?.focus();
    },

    searchPatients() {
      if (!this.searchTerm || this.searchTerm.length < 2) {
        return;
      }

      this.searching = true;
      this.hasSearched = true;

      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("search", this.searchTerm);
      data.append("searchMode", "all");
      data.append("limit", "10");

      execute(data, hospital)
          .then((res) => {
            this.searching = false;
            if (res.data.success) {
              this.searchResults = res.data.data.patients || [];
            } else {
              this.searchResults = [];
            }
          })
          .catch((e) => {
            this.searching = false;
            console.error(e);
          });
    },

    loadPatientByID(patientID) {
      // Show loading state
      this.searching = true;

      const data = new FormData();
      data.append("function", "getPatient");
      data.append("patientID", patientID);

      execute(data, hospital)
          .then((res) => {
            this.searching = false;
            if (res.data.success) {
              // Handle different response structures
              const patient = res.data.data.patient || res.data.data;
              if (patient && patient.patientID) {
                this.selectPatient(patient);
              } else {
                // Fallback: try searchPatient
                this.searchPatientByID(patientID);
              }
            } else {
              // Fallback: try searchPatient
              this.searchPatientByID(patientID);
            }
          })
          .catch((e) => {
            this.searching = false;
            console.error('Error loading patient:', e);
            // Fallback: try searchPatient
            this.searchPatientByID(patientID);
          });
    },

    searchPatientByID(patientID) {
      const data = new FormData();
      data.append("function", "searchPatient");
      data.append("search", patientID);
      data.append("searchMode", "patientID");

      execute(data, hospital)
          .then((res) => {
            this.searching = false;
            if (res.data.success) {
              const patients = res.data.data.patients || [];
              if (patients.length > 0) {
                this.selectPatient(patients[0]);
              } else if (res.data.data.patient) {
                this.selectPatient(res.data.data.patient);
              }
            }
          })
          .catch((e) => {
            this.searching = false;
            console.error('Error searching patient:', e);
          });
    },

    selectPatient(patient) {
      this.selectedPatient = patient;
      this.searchResults = [];
      this.loadPatientInsurance(patient.patientID);
      this.loadRecentVisits(patient.patientID);
    },

    clearPatient() {
      this.selectedPatient = null;
      this.patientInsurances = [];
      this.recentVisits = [];
      this.visit.insuranceID = '';
      this.searchTerm = '';
      this.hasSearched = false;
    },

    loadPatientInsurance(patientID) {
      const data = new FormData();
      data.append("function", "getPatientInsurance");
      data.append("patientID", patientID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.patientInsurances = res.data.data.insurances || [];
              // Auto-select primary insurance
              const primary = this.patientInsurances.find(i => i.isPrimary === 'Yes');
              if (primary) this.visit.insuranceID = primary.insuranceID;
            }
          })
          .catch((e) => console.error(e));
    },

    loadRecentVisits(patientID) {
      const data = new FormData();
      data.append("function", "getPatientHistory");
      data.append("patientID", patientID);
      data.append("type", "visits");

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.recentVisits = res.data.data.visits || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      data.append("facilityID", this.facilityID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            }
          })
          .catch((e) => console.error(e));
    },

    createVisit() {
      if (!this.canSubmit) return;

      this.submitting = true;
      this.generatedBillNo = '';

      const data = new FormData();
      data.append("function", "createVisit");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("patientID", this.selectedPatient.patientID);
      data.append("patientNo", this.selectedPatient.patientID);
      data.append("patientName", this.selectedPatient.fullNames);
      data.append("visitType", this.visit.visitType);
      data.append("visitCategory", this.visit.visitCategory);
      data.append("departmentID", this.visit.departmentID);
      data.append("paymentMode", this.visit.paymentMode);
      data.append("insuranceID", this.visit.insuranceID);
      data.append("chiefComplaint", this.visit.chiefComplaint);
      data.append("referralNo", this.visit.referralNo);
      data.append("createdBy", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              // Handle response
              const responseData = res.data.data || {};
              this.createdVisit = responseData.visit || {
                visitID: responseData.visitID || '',
                visitNo: responseData.visitNo || responseData.visitID || '',
                queueNo: responseData.queueNo || responseData.queueNumber || '001'
              };

              // Ensure queueNo is set
              if (!this.createdVisit.queueNo) {
                this.createdVisit.queueNo = responseData.queueNo || responseData.queueNumber || '001';
              }

              // If billing consultation, add charge and generate invoice
              if (this.billConsultation && this.selectedFeeData && this.consultationAmount > 0) {
                this.addConsultationCharge(this.createdVisit.visitID || responseData.visitID);
              } else {
                // No billing, just show success
                this.submitting = false;
                this.showSuccessModal();
              }
            } else {
              this.submitting = false;
              this.$refs.toast.error(res.data.message || 'Failed to create visit');
            }
          })
          .catch((e) => {
            this.submitting = false;
            console.error(e);
            this.$refs.toast.error('Error creating visit');
          });
    },

    // ==========================================
    // CONSULTATION FEE BILLING METHODS
    // ==========================================

    loadIncomeTypes() {
      this.loadingFees = true;

      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            this.loadingFees = false;
            if (res.data.success) {
              this.incomeTypes = res.data.data.incomeTypes || [];

              // Auto-select OPD or consultation-related income type if found
              const consultationType = this.incomeTypes.find(t =>
                  t.incomeTypeDescription?.toLowerCase().includes('opd') ||
                  t.incomeTypeDescription?.toLowerCase().includes('consultation') ||
                  t.incomeTypeDescription?.toLowerCase().includes('hospital')
              );

              if (consultationType) {
                this.selectedIncomeTypeId = consultationType.incomeTypeId;
                this.onIncomeTypeChange();
              }
            }
          })
          .catch((e) => {
            this.loadingFees = false;
            console.error('Error loading income types:', e);
          });
    },

    onIncomeTypeChange() {
      if (!this.selectedIncomeTypeId) {
        this.consultationFees = [];
        this.selectedConsultationFee = '';
        this.selectedFeeData = null;
        this.consultationAmount = 0;
        return;
      }

      this.loadFeesForIncomeType(this.selectedIncomeTypeId);
    },

    loadFeesForIncomeType(incomeTypeId) {
      this.loadingFees = true;
      this.consultationFees = [];
      this.selectedConsultationFee = '';
      this.selectedFeeData = null;
      this.consultationAmount = 0;

      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingFees = false;
            if (res.data.success) {
              // Show ALL fees for this income type (no filtering)
              this.consultationFees = res.data.data.feesAndCharges || [];

              // Auto-select first fee if available
              if (this.consultationFees.length > 0) {
                this.selectedConsultationFee = JSON.stringify(this.consultationFees[0]);
                this.onConsultationFeeChange();
              }
            }
          })
          .catch((e) => {
            this.loadingFees = false;
            console.error('Error loading fees:', e);
          });
    },

    onConsultationFeeChange() {
      if (this.selectedConsultationFee) {
        this.selectedFeeData = JSON.parse(this.selectedConsultationFee);
        this.consultationAmount = parseFloat(this.selectedFeeData.unitFeeAmount) || 0;
      } else {
        this.selectedFeeData = null;
        this.consultationAmount = 0;
      }
    },

    addConsultationCharge(visitID) {
      // Step 1: Add charge to visit_charges table
      const chargeData = new FormData();
      chargeData.append("function", "addVisitCharge");
      chargeData.append("facilityID", this.facilityID);
      chargeData.append("facilityName", this.facilityName);
      chargeData.append("visitID", visitID);
      chargeData.append("patientID", this.selectedPatient.patientID);
      chargeData.append("patientName", this.selectedPatient.fullNames);
      chargeData.append("serviceCategory", "CONSULTATION");
      chargeData.append("serviceName", this.selectedFeeData.feeDescription);
      chargeData.append("serviceCode", this.selectedFeeData.feeId || '');
      chargeData.append("referenceType", "visits");
      chargeData.append("referenceID", visitID);
      chargeData.append("sourceModule", "OPD");
      chargeData.append("quantity", 1);
      chargeData.append("unitPrice", this.consultationAmount);
      chargeData.append("paymentMode", this.visit.paymentMode);
      chargeData.append("insuranceID", this.visit.insuranceID || '');
      chargeData.append("billerFeeID", this.selectedFeeData.feeId || '');
      chargeData.append("billerIncomeTypeID", this.selectedFeeData.incomeTypeId || '');
      chargeData.append("billerIncomeTypeDesc", this.selectedFeeData.incomeTypeDescription || '');
      chargeData.append("sessionNames", this.sessionNames);

      execute(chargeData, hospital)
          .then((res) => {
            if (res.data.success) {
              // Step 2: Generate invoice in biller
              this.generateConsultationInvoice(visitID);
            } else {
              // Charge failed, but visit was created - proceed anyway
              console.error('Failed to add charge:', res.data.message);
              this.submitting = false;
              this.showSuccessModal();
            }
          })
          .catch((e) => {
            console.error('Error adding charge:', e);
            this.submitting = false;
            this.showSuccessModal();
          });
    },

    generateConsultationInvoice(visitID) {
      // Build bill item for biller
      const billItems = [{
        feeDescription: this.selectedFeeData.feeDescription,
        quantity: 1,
        unitFeeAmount: this.consultationAmount,
        amount: this.consultationAmount,
        feeId: this.selectedFeeData.feeId,
        incomeTypeId: this.selectedFeeData.incomeTypeId,
        fiscalYear: new Date().getFullYear().toString(),
        customer: `Visit: ${this.createdVisit.visitNo}`,
        customerName: this.selectedPatient.fullNames,
        customerPhoneNumber: this.selectedPatient.phoneNumber || '',
        zone: this.zone
      }];

      const billData = new FormData();
      billData.append("function", "generateBill3");
      billData.append("penalized", false);
      billData.append("billItem", JSON.stringify(billItems));
      billData.append("payNow", "true");

      execute(billData, biller)
          .then((res) => {
            this.submitting = false;
            if (res.data.success) {
              this.generatedBillNo = res.data.data.bill?.billDetails?.billNo || '';

              // Update visit with bill number
              if (this.generatedBillNo) {
                this.updateVisitBillNo(visitID, this.generatedBillNo);
              }
            }
            this.showSuccessModal();
          })
          .catch((e) => {
            this.submitting = false;
            console.error('Error generating invoice:', e);
            this.showSuccessModal();
          });
    },

    updateVisitBillNo(visitID, billNo) {
      // Update visit with generated bill number
      const data = new FormData();
      data.append("function", "updateVisit");
      data.append("visitID", visitID);
      data.append("billNo", billNo);
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital).catch(e => console.error('Error updating visit bill:', e));

      // Mark the charge as billed
      const updateData = new FormData();
      updateData.append("function", "updateVisitChargesByVisit");
      updateData.append("visitID", visitID);
      updateData.append("billNo", billNo);
      updateData.append("billed", "Yes");
      updateData.append("status", "Billed");
      updateData.append("sessionNames", this.sessionNames);

      execute(updateData, hospital).catch(e => console.error('Error updating charges:', e));
    },

    showSuccessModal() {
      const modal = new bootstrap.Modal(document.getElementById('successModal'));
      modal.show();
    },

    printTicket() {
      const printContent = `
        <html>
        <head>
          <title>Queue Ticket</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .ticket { max-width: 300px; margin: 0 auto; border: 2px dashed #333; padding: 20px; }
            .hospital-name { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
            .queue-number { font-size: 72px; font-weight: bold; color: #0066cc; margin: 20px 0; }
            .patient-name { font-size: 16px; margin-bottom: 10px; }
            .visit-info { font-size: 12px; color: #666; }
            .bill-info { margin-top: 15px; padding-top: 10px; border-top: 1px dashed #ccc; }
            .bill-amount { font-size: 18px; font-weight: bold; color: #28a745; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="hospital-name">${this.facilityName}</div>
            <hr>
            <div class="queue-number">${this.createdVisit?.queueNo || '001'}</div>
            <div class="patient-name">${this.selectedPatient?.fullNames}</div>
            <div class="visit-info">
              <p>Visit: ${this.createdVisit?.visitNo}</p>
              <p>Type: ${this.visit.visitType}</p>
              <p>Date: ${new Date().toLocaleDateString()}</p>
            </div>
            ${this.generatedBillNo ? `
              <div class="bill-info">
                <p>Invoice: ${this.generatedBillNo}</p>
                <p class="bill-amount">KES ${this.formatCurrency(this.consultationAmount)}</p>
                <p style="font-size: 11px;">Please pay at cashier</p>
              </div>
            ` : ''}
            <hr>
            <p style="font-size: 10px;">Please wait for your number to be called</p>
          </div>
        </body>
        </html>
      `;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    },

    printBill() {
      if (this.generatedBillNo) {
        localStorage['params'] = JSON.stringify({ billNo: this.generatedBillNo });
        const routeData = this.$router.resolve({ name: 'bill' });
        window.open(routeData.href, '_blank');
      }
    },

    resetForm() {
      this.visit = {
        visitType: '',
        visitCategory: 'GENERAL',
        departmentID: '',
        paymentMode: '',
        insuranceID: '',
        chiefComplaint: '',
        referralNo: ''
      };
      // Reset billing state but keep selected fee
      this.generatedBillNo = '';
      this.createdVisit = null;
    },

    newVisit() {
      bootstrap.Modal.getInstance(document.getElementById('successModal'))?.hide();
      this.selectedPatient = null;
      this.patientInsurances = [];
      this.recentVisits = [];
      this.searchTerm = '';
      this.searchResults = [];
      this.hasSearched = false;
      this.resetForm();
      this.generatedBillNo = '';
    },

    goToQueue() {
      bootstrap.Modal.getInstance(document.getElementById('successModal')).hide();
      this.$router.push('/queue-management');
    },

    goToPatientInsurance() {
      if (this.selectedPatient) {
        this.$router.push('/patient-insurance/' + this.selectedPatient.patientID);
      }
    }
  },
  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.zone = sessionStorage.getItem("sessionZone") || '';

    this.loadDepartments();
    this.loadIncomeTypes();

    // Focus search input
    this.$nextTick(() => {
      this.$refs.searchInput?.focus();
    });

    // Check for pre-selected patient from route query, params, or props
    // Supports: ?patientID=xxx, /new-visit/:id, or prop
    const patientID = this.$route.query.patientID || this.$route.params.id || this.patientID;
    if (patientID) {
      this.loadPatientByID(patientID);
    }
  }
};
</script>

<style scoped>
/* Search Box */
.search-box {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.search-box:focus-within {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.search-box .input-group-text {
  background: #fff;
  border: none;
  padding-left: 1rem;
}

.search-box .form-control {
  border: none;
  font-size: 1rem;
  padding: 0.75rem 0.5rem;
}

.search-box .form-control:focus {
  box-shadow: none;
}

.search-box .btn {
  border-radius: 0;
}

/* Search Examples */
.search-example {
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8rem;
}

/* Avatar Styles */
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-md { width: 3.5rem; height: 3.5rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
  color: #fff;
}

/* Colors */
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-pink-subtle { background-color: rgba(232, 62, 140, 0.15); }
.bg-pink { background-color: #e83e8c !important; }
.text-pink { color: #e83e8c !important; }

/* Search Results */
.search-results {
  max-height: 300px;
  overflow-y: auto;
}

.list-group-item {
  border-left: none;
  border-right: none;
  transition: all 0.2s ease;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

/* Empty State */
.empty-state-icon {
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.empty-state-icon i {
  font-size: 28px;
  color: #adb5bd;
}

/* Insurance Option */
.insurance-option {
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.insurance-option:hover {
  border-color: #556ee6 !important;
  background: #f8f9ff;
}

.insurance-option.selected {
  background: rgba(85, 110, 230, 0.05);
}

/* Queue Number Box */
.queue-number-box {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  display: inline-block;
}

.queue-number {
  font-size: 64px;
  font-weight: 700;
  color: #556ee6;
  line-height: 1;
}

/* Card styling */
.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
</style>